import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { animate, style, transition, trigger } from '@angular/animations';
import { NotifyService } from '@app/Services/NotifyService';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ClientDataStore } from '@app/Global/ClientDataStore';
import { ApiService } from '@app/Services/APIService';
import { AccountsControllerMethods, PaymentManagementControllerMethods, UsersControllerMethods } from '@app/Global/EnumManager';
import { DocViewer } from '../DocViewer/DocViewer';
import { LoanIndex } from '../LoanIndex/LoanIndex';

@Component({
    selector: 'ClientSelection',
    templateUrl: './ClientSelection.html',
    styleUrls: ['./ClientSelection.scss'],
    animations: [
        trigger('fadeInSection', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('1.0s ease-out', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class ClientSelection implements OnInit, AfterViewInit {

    //Constructor
    constructor(public globalFunctions: GlobalFunctions,
        private notifyService: NotifyService,
        public dialog: MatDialog,
        private clientDataStore: ClientDataStore,
        private apiService: ApiService,
        private dialogRef: MatDialogRef<ClientSelection>) {
    }

    //Unique identifier to the launched modal
    @Input() ModalIdentifier;

    @Input() LoanIndex: LoanIndex;

    //BTN_GenerateBankDetailsLetter
    @Input() GenerateLetterButtonID = "";

    //BTN_GenerateBankDetailsLetter
    @Input() ToastTitle = "";

    public PaymentManagement;

    public AccountID;
    //To store the selected option
    public SelectedMultiselectSingleOptions = [];

    //List of available clients for users to select in the dropdown
    public ClientMultiselectOptions = [];

    //To store the list of clients linked to the account
    public LinkedClients = [];

    //For tracking if a server update request is in progress
    public IsGenerateInProgress = false;

    //Multiselect single Input
    @ViewChild('INP_MultiselectSingle') INPMultiselectSingle;

    //This will be called after the view has been loaded
    ngAfterViewInit() {

        //We still need a short delay even this method is added on ngAfterViewInit
        this.globalFunctions.delay(50).then(() => {
            this.LinkedClients_Get();
        });
    }

    public FeeBeingEdited = false;

    ngOnInit(): void {

        //InitPage
        this.Page_Init();
    }


    //Setting defaults on initial load
    private Page_Init(): void {

        //Initialise the client selection options: Add the linked clients for the multiselect option
        if (!this.globalFunctions.isEmpty(this.LinkedClients)) {

            //Clear the options first
            this.ClientMultiselectOptions = [];

            //Loop through each client and add to the multiselect options
            this.LinkedClients.forEach(client => {
                this.ClientMultiselectOptions.push({ name: this.globalFunctions.HTMLUnescape(client.DisplayName), GUID: client.GUID });
            });
        }
    }

    //Toggle when we an item is selected from the list. Allowed = Single only
    public MultiSelectSingle_Toggle(e) {

        //Empty the array if there is any selection option already
        if (!this.globalFunctions.isEmpty(this.SelectedMultiselectSingleOptions)) {
            this.SelectedMultiselectSingleOptions.length = 0;
            this.SelectedMultiselectSingleOptions = [];
        }

        if (!this.globalFunctions.isEmpty(e.itemValue)) {

            //Set the new value in the selection option
            this.SelectedMultiselectSingleOptions.push(e.itemValue);

            //Hide the panel on selection
            if (!this.globalFunctions.isEmpty(this.INPMultiselectSingle)) {
                this.INPMultiselectSingle.hide();
            }
        }
    }

    public GenerateLetter(): void {
        //Let's check which method to call based on the buttons
        if (this.GenerateLetterButtonID == "GenerateDDConfirmationLetter") {
            this.DDConfirmationLetter_Generate();
        }

        //Let's check which method to call based on the buttons
        if (this.GenerateLetterButtonID == "GenerateBPAYLetter") {
            this.BPAYLetter_Generate();
        }
    }

    public BPAYLetter_Generate(): void {

        //Validate the client selection
        if (this.globalFunctions.isEmpty(this.SelectedMultiselectSingleOptions)) {
            this.notifyService.Error_Show("Please select a client", "Error");
            return;
        }

        const selectedClient = this.SelectedMultiselectSingleOptions[0];
        if (this.globalFunctions.isEmpty(selectedClient) || this.globalFunctions.isEmpty(selectedClient.GUID)) {
            this.notifyService.Error_Show("Please select a client", "Error");
            return;
        }

        // Here you would implement the actual letter generation logic
        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct the request and send it to the server
        const apiRequest = { AccountID: this.AccountID, ClientGUID: selectedClient.GUID };

        //API to generate BPAY letter
        this.apiService.APIData_Post(this.apiService.Endpoints.PaymentManagementController, PaymentManagementControllerMethods[PaymentManagementControllerMethods.GenerateBPAYLetter], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }

                else {
                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Check the response and process the base64 string
                    //Create a blob that can be used to represent the binary version of the file
                    if (!this.globalFunctions.isEmpty(response.Base64Document)) {

                        //Convert base64 string to Blob
                        const blob = this.globalFunctions.base64toBlob(response.Base64Document);

                        //Open DocViewer
                        const docViewer = this.globalFunctions.FeatureModal_Launch(DocViewer, this.globalFunctions.GetFeatureModalConfig('90%', false, false, "glb_fixedHeightDialog"), this.dialog, "Doc Viewer", this.LoanIndex.AccountID, true, false);
                        docViewer.DialogRef.componentInstance.FileBlob = blob;
                        docViewer.DialogRef.componentInstance.FileName = response.Name + "." + response.Extension;
                        docViewer.DialogRef.componentInstance.FileType = response.Extension;
                        docViewer.DialogRef.componentInstance.OriginalFileType = response.OriginalFileType;

                        //These 3 datapoints are required for downloading a document from the DocViewer
                        docViewer.DialogRef.componentInstance.DocumentGUID = response.GUID;
                        docViewer.DialogRef.componentInstance.EntityType = "LoanDocuments";
                        docViewer.DialogRef.componentInstance.LoanIndex = this.LoanIndex;

                        this.globalFunctions.delay(1000).then(() => {

                            //Show toast message on success
                            this.notifyService.Success_Show("The " + this.ToastTitle + " has been generated successfully.", this.ToastTitle);
                        });
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }

    //Generate DD Confirmation Letter
    public DDConfirmationLetter_Generate(): void {

        //Validate the client selection
        if (this.globalFunctions.isEmpty(this.SelectedMultiselectSingleOptions)) {
            this.notifyService.Error_Show("Please select a client", "Error");
            return;
        }

        const selectedClient = this.SelectedMultiselectSingleOptions[0];
        if (this.globalFunctions.isEmpty(selectedClient) || this.globalFunctions.isEmpty(selectedClient.GUID)) {
            this.notifyService.Error_Show("Please select a client", "Error");
            return;
        }

        // Here you would implement the actual letter generation logic
        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct the request and send it to the server
        const apiRequest = { AccountID: this.AccountID, ClientGUID: selectedClient.GUID };

        //API to generate DD confimrmation letter
        this.apiService.APIData_Post(this.apiService.Endpoints.PaymentManagementController, PaymentManagementControllerMethods[PaymentManagementControllerMethods.GenerateDDConfirmationLetter], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }

                else {
                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Check the response and process the base64 string
                    //Create a blob that can be used to represent the binary version of the file
                    if (!this.globalFunctions.isEmpty(response.Base64Document)) {

                        //Convert base64 string to Blob
                        const blob = this.globalFunctions.base64toBlob(response.Base64Document);

                        //Open DocViewer
                        const docViewer = this.globalFunctions.FeatureModal_Launch(DocViewer, this.globalFunctions.GetFeatureModalConfig('90%', false, false, "glb_fixedHeightDialog"), this.dialog, "Doc Viewer", this.LoanIndex.AccountID, true, false);
                        docViewer.DialogRef.componentInstance.FileBlob = blob;
                        docViewer.DialogRef.componentInstance.FileName = response.Name + "." + response.Extension;
                        docViewer.DialogRef.componentInstance.FileType = response.Extension;
                        docViewer.DialogRef.componentInstance.OriginalFileType = response.OriginalFileType;

                        //These 3 datapoints are required for downloading a document from the DocViewer
                        docViewer.DialogRef.componentInstance.DocumentGUID = response.GUID;
                        docViewer.DialogRef.componentInstance.EntityType = "LoanDocuments";
                        docViewer.DialogRef.componentInstance.LoanIndex = this.LoanIndex;

                        this.globalFunctions.delay(1000).then(() => {

                            //Show toast message on success
                            this.notifyService.Success_Show("The " + this.ToastTitle + " has been generated successfully.", this.ToastTitle);
                        });
                    }

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }

    //This closes the modal
    public ClientSelection_CloseModal(): void {

        //Destroy this using angular material
        this.globalFunctions.FeatureModal_Close(this.ModalIdentifier);
    }

    //Method to invoke API to get all linked individual clients
    private LinkedClients_Get(): void {

        //Track state of this request, so that we can show the spinner, if necessary.
        this.IsGenerateInProgress = true;
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct the request and send it to the server
        const apiRequest = {
            AccountID: this.AccountID
        };

        this.apiService.APIData_Post(this.apiService.Endpoints.AccountsController, AccountsControllerMethods[AccountsControllerMethods.GetAccountLinkedOptedClients], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    this.IsGenerateInProgress = false;
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    return;
                }
                else {
                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Set the target clients with the server response
                    this.LinkedClients = response.LinkedClients;

                    //Now initialise the page
                    this.Page_Init();

                    //Turn off the fullscreen loading
                    this.clientDataStore.SetShowFullscreenLoading(false);
                    this.IsGenerateInProgress = false;
                    return;
                }
            });
    }
}